// Klient API generatorPDF. Same-origin: nginx proxuje /api → platform-api (ten sam backend co
// aihub.ltca.pl — wspólne logowanie/JWT, decyzja z planu budowy). Wzorzec 1:1 z platform-web/src/api.js.
const TOKEN_KEY = 'gpdf_token'
const REFRESH_KEY = 'gpdf_refresh'

export const getToken = () => localStorage.getItem(TOKEN_KEY)
export const getRefresh = () => localStorage.getItem(REFRESH_KEY)
export const setTokens = (a, r) => { localStorage.setItem(TOKEN_KEY, a); if (r) localStorage.setItem(REFRESH_KEY, r) }
export const clearTokens = () => { localStorage.removeItem(TOKEN_KEY); localStorage.removeItem(REFRESH_KEY) }

let _refreshPromise = null
function refreshAccess() {
  const rt = getRefresh()
  if (!rt) return Promise.reject(new Error('brak refresh tokenu'))
  if (!_refreshPromise) {
    _refreshPromise = fetch('/api/auth/refresh', {
      method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ refresh_token: rt }).toString(),
    }).then(async r => {
      if (!r.ok) throw new Error('refresh nieudany')
      const d = await r.json(); setTokens(d.access_token, d.refresh_token); return d.access_token
    }).finally(() => { _refreshPromise = null })
  }
  return _refreshPromise
}

function logoutBroadcast() {
  clearTokens()
  if (typeof window !== 'undefined') window.dispatchEvent(new Event('gpdf:logout'))
}

async function req(path, opts = {}) {
  const { method = 'GET', json, form, body, auth = true, _retried = false } = opts
  const headers = {}
  if (auth) { const t = getToken(); if (t) headers['Authorization'] = `Bearer ${t}` }
  let payload
  if (json !== undefined) { headers['Content-Type'] = 'application/json'; payload = JSON.stringify(json) }
  else if (form !== undefined) { headers['Content-Type'] = 'application/x-www-form-urlencoded'; payload = new URLSearchParams(form).toString() }
  else if (body !== undefined) { payload = body }

  let res
  try {
    res = await fetch(`/api${path}`, { method, headers, body: payload })
  } catch {
    const e = new Error('Brak połączenia z serwerem — sprawdź połączenie i spróbuj ponownie.'); e.status = 0; throw e
  }

  if (res.status === 401 && auth && !_retried && getRefresh()) {
    try { await refreshAccess(); return req(path, { ...opts, _retried: true }) }
    catch { logoutBroadcast() }
  }

  if (res.status === 204) return null
  const ct = res.headers.get('content-type') || ''
  let data
  try { data = ct.includes('application/json') ? await res.json() : await res.text() }
  catch { data = null }
  if (!res.ok) {
    let msg
    if (data && typeof data === 'object' && data.detail) {
      msg = Array.isArray(data.detail) ? data.detail.map(d => d.msg).join('; ') : data.detail
    } else if ([502, 503, 504].includes(res.status)) {
      msg = 'Błąd połączenia z serwerem — spróbuj ponownie za chwilę.'
    } else if (typeof data === 'string' && data.trim() && !/<!doctype|<\/?(html|body|head|center)\b/i.test(data)) {
      msg = data.trim().slice(0, 300)
    } else {
      msg = `Błąd ${res.status} — spróbuj ponownie.`
    }
    const e = new Error(msg); e.status = res.status; throw e
  }
  return data
}

// pobranie wyniku joba (PDF/PPTX) jako plik, z nazwą i rozszerzeniem z Content-Disposition/formatu
export async function downloadJobResult(id, filename) {
  const t = getToken()
  const res = await fetch(`/api/jobs/${id}/result`, { headers: t ? { Authorization: `Bearer ${t}` } : {} })
  if (!res.ok) throw new Error('Nie można pobrać wyniku')
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = filename || 'wynik'
  document.body.appendChild(a); a.click(); a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 10000)
}

export const api = {
  login: (u, p) => req('/auth/login', { method: 'POST', form: { username: u, password: p }, auth: false }),
  me: () => req('/me'),
  job: (id) => req(`/jobs/${id}`),
  jobResult: (id) => req(`/jobs/${id}/result`),

  // --- Szablony (admin) ---
  templates: () => req('/pdfgen/templates'),
  template: (id) => req(`/pdfgen/templates/${id}`),
  uploadTemplate: (fd, params) => req(`/pdfgen/templates?${new URLSearchParams(params)}`, { method: 'POST', body: fd }),
  patchTemplate: (id, b) => req(`/pdfgen/templates/${id}`, { method: 'PATCH', json: b }),
  deleteTemplate: (id) => req(`/pdfgen/templates/${id}`, { method: 'DELETE' }),
  patchPartial: (id, b) => req(`/pdfgen/partials/${id}`, { method: 'PATCH', json: b }),
  patchSlot: (id, b) => req(`/pdfgen/slots/${id}`, { method: 'PATCH', json: b }),
  publishTemplate: (id) => req(`/pdfgen/templates/${id}/publish`, { method: 'POST' }),
  renderTest: (partialId, b) => req(`/pdfgen/partials/${partialId}/render-test`, { method: 'POST', json: b }),
  previewPartial: (partialId, activeSlot) => req(`/pdfgen/partials/${partialId}/preview${activeSlot ? `?active_slot=${encodeURIComponent(activeSlot)}` : ''}`),
  designTemplate: (b) => req('/pdfgen/templates/design', { method: 'POST', json: b }),
  reviewTemplate: (id) => req(`/pdfgen/templates/${id}/review`, { method: 'POST' }),
  aiFillPromptsTemplate: (id) => req(`/pdfgen/templates/${id}/ai-fill-prompts`, { method: 'POST' }),

  // --- Projekty ---
  projects: () => req('/pdfgen/projects'),
  project: (id) => req(`/pdfgen/projects/${id}`),
  createProject: (b) => req('/pdfgen/projects', { method: 'POST', json: b }),
  deleteProject: (id) => req(`/pdfgen/projects/${id}`, { method: 'DELETE' }),
  adminProjects: () => req('/pdfgen/admin/projects'),
  importFile: (id, fd) => req(`/pdfgen/projects/${id}/import-file`, { method: 'POST', body: fd }),
  importText: (id, b) => req(`/pdfgen/projects/${id}/import-text`, { method: 'POST', json: b }),
  units: (id) => req(`/pdfgen/projects/${id}/units`),
  // Aktywny (queued/running) job dla projektu (import/kompozycja) — do wznowienia polowania po
  // odświeżeniu strony (Faza 10). Brak aktywnego joba to normalny stan (404), nie błąd — zwracamy null.
  activeJob: async (id) => { try { return await req(`/pdfgen/projects/${id}/active-job`) } catch (e) { if (e.status === 404) return null; throw e } },
  patchUnit: (projId, unitId, content) => req(`/pdfgen/projects/${projId}/units/${unitId}`, { method: 'PATCH', json: { content } }),
  uploadUnitImage: (projId, unitId, fd) => req(`/pdfgen/projects/${projId}/units/${unitId}/image`, { method: 'POST', body: fd }),

  // --- Composition Engine ---
  compose: (id) => req(`/pdfgen/projects/${id}/compose`, { method: 'POST' }),
  fillImages: (id) => req(`/pdfgen/projects/${id}/fill-images`, { method: 'POST' }),
  pages: (id) => req(`/pdfgen/projects/${id}/pages`),
  unassigned: (id) => req(`/pdfgen/projects/${id}/unassigned`),
  assignUnassigned: (projId, unassignedId, b) => req(`/pdfgen/projects/${projId}/unassigned/${unassignedId}/assign`, { method: 'POST', json: b }),
  changePartial: (projId, pageId, partialId) => req(`/pdfgen/projects/${projId}/pages/${pageId}/change-partial`, { method: 'POST', json: { partial_id: partialId } }),
  preview: (id) => req(`/pdfgen/projects/${id}/preview`),

  // --- Eksport ---
  exportProject: (id, format) => req(`/pdfgen/projects/${id}/export?format=${encodeURIComponent(format)}`, { method: 'POST' }),

  // --- Wersjonowanie / duplikowanie ---
  versions: (id) => req(`/pdfgen/projects/${id}/versions`),
  saveVersion: (id, label) => req(`/pdfgen/projects/${id}/versions`, { method: 'POST', json: { label } }),
  restoreVersion: (id, v) => req(`/pdfgen/projects/${id}/versions/${v}/restore`, { method: 'POST' }),
  duplicateProject: (id) => req(`/pdfgen/projects/${id}/duplicate`, { method: 'POST' }),
  saveAsProject: (id, name) => req(`/pdfgen/projects/${id}/save-as`, { method: 'POST', json: { name } }),

  // --- Komentarze ---
  comments: (projId, unitId) => req(`/pdfgen/projects/${projId}/units/${unitId}/comments`),
  addComment: (projId, unitId, body) => req(`/pdfgen/projects/${projId}/units/${unitId}/comments`, { method: 'POST', json: { body } }),
  patchComment: (projId, commentId, status) => req(`/pdfgen/projects/${projId}/comments/${commentId}`, { method: 'PATCH', json: { status } }),

  // --- Historia ---
  history: (id) => req(`/pdfgen/projects/${id}/history`),

  // --- Udostępnianie ---
  shares: (id) => req(`/pdfgen/projects/${id}/shares`),
  addShare: (id, userId, role) => req(`/pdfgen/projects/${id}/shares`, { method: 'POST', json: { user_id: userId, role } }),
  removeShare: (id, shareId) => req(`/pdfgen/projects/${id}/shares/${shareId}`, { method: 'DELETE' }),

  // --- Wyszukiwarka użytkowników (udostępnianie / admin: tworzenie projektu dla kogoś) ---
  searchUsers: (q) => req(`/pdfgen/users/search?q=${encodeURIComponent(q)}`),

  // --- Admin: użytkownicy (do wyszukania kogo udostępnić) ---
  adminUsers: () => req('/admin/users'),

  // --- Config publiczny (Faza 8.4) ---
  publicConfig: () => req('/v1/config', { auth: false }),

  // --- Auth: rejestracja / weryfikacja e-mail / zaproszenia (Faza 8.5) ---
  register: (b) => req('/auth/register', { method: 'POST', json: b, auth: false }),
  verifyEmail: (token) => req('/auth/verify-email', { method: 'POST', json: { token }, auth: false }),
  getInvite: (token) => req(`/organizations/invites/${token}`, { auth: false }),
  acceptInvite: (token, b) => req(`/organizations/invites/${token}/accept`, { method: 'POST', json: b || {}, auth: false }),

  // --- Billing (klient, Faza 8.6) ---
  plans: () => req('/billing/plans'),
  billingMe: () => req('/billing/me'),
  checkout: (planId, successUrl, cancelUrl) => req('/billing/checkout', { method: 'POST', json: { plan_id: planId, success_url: successUrl, cancel_url: cancelUrl } }),
  billingPortal: () => req('/billing/portal', { method: 'POST' }),

  // --- Billing (admin: plany, Faza 8.6) ---
  adminPlans: () => req('/admin/plans'),
  createPlan: (b) => req('/admin/plans', { method: 'POST', json: b }),
  patchPlan: (id, b) => req(`/admin/plans/${id}`, { method: 'PATCH', json: b }),
  archivePlan: (id) => req(`/admin/plans/${id}/archive`, { method: 'POST' }),

  // --- Organizacje (Faza 8.7) ---
  organizations: () => req('/organizations'),
  organization: (id) => req(`/organizations/${id}`),
  createOrganization: (b) => req('/organizations', { method: 'POST', json: b }),
  deleteOrganization: (id) => req(`/organizations/${id}`, { method: 'DELETE' }),
  orgMembers: (id) => req(`/organizations/${id}/members`),
  addOrgMember: (id, userId, role) => req(`/organizations/${id}/members`, { method: 'POST', json: { user_id: userId, role } }),
  updateOrgMemberRole: (orgId, memberId, role) => req(`/organizations/${orgId}/members/${memberId}`, { method: 'PATCH', json: { role } }),
  removeOrgMember: (orgId, memberId) => req(`/organizations/${orgId}/members/${memberId}`, { method: 'DELETE' }),
  inviteOrgMember: (orgId, b) => req(`/organizations/${orgId}/invites`, { method: 'POST', json: b }),

  // --- Powiadomienia (Faza 8.4/8.8) ---
  notifications: (params) => req(`/notifications${params ? `?${new URLSearchParams(params)}` : ''}`),
  unreadCount: () => req('/notifications/unread-count'),
  markNotificationRead: (id) => req(`/notifications/${id}/read`, { method: 'POST' }),
  markAllNotificationsRead: () => req('/notifications/read-all', { method: 'POST' }),

  // --- Admin: ustawienia systemowe / sekrety (Faza 8.8) ---
  adminSettings: () => req('/admin/settings'),
  putAdminSetting: (key, b) => req(`/admin/settings/${encodeURIComponent(key)}`, { method: 'PUT', json: b }),
  adminSecrets: () => req('/admin/secrets'),
  putAdminSecret: (key, b) => req(`/admin/secrets/${encodeURIComponent(key)}`, { method: 'PUT', json: b }),
  deleteAdminSecret: (key) => req(`/admin/secrets/${encodeURIComponent(key)}`, { method: 'DELETE' }),

  // --- Admin: szablony e-mail (Faza 8.8) ---
  emailTemplates: () => req('/admin/email-templates'),
  emailTemplate: (key, lang) => req(`/admin/email-templates/${key}/${lang}`),
  putEmailTemplate: (key, lang, b) => req(`/admin/email-templates/${key}/${lang}`, { method: 'PUT', json: b }),
  deleteEmailTemplate: (key, lang) => req(`/admin/email-templates/${key}/${lang}`, { method: 'DELETE' }),
  testEmailTemplate: (toAddress) => req('/admin/email-templates/test-email', { method: 'POST', json: { to_address: toAddress } }),

  // --- Admin: i18n (klucze/tłumaczenia, Faza 8.8) ---
  i18nKeys: () => req('/admin/i18n/keys'),
  createI18nKey: (b) => req('/admin/i18n/keys', { method: 'POST', json: b }),
  patchI18nKey: (id, b) => req(`/admin/i18n/keys/${id}`, { method: 'PATCH', json: b }),
  i18nTranslations: (params) => req(`/admin/i18n/translations${params ? `?${new URLSearchParams(params)}` : ''}`),
  createTranslation: (b) => req('/admin/i18n/translations', { method: 'POST', json: b }),
  patchTranslation: (id, b) => req(`/admin/i18n/translations/${id}`, { method: 'PATCH', json: b }),
  aiSuggestTranslation: (id) => req(`/admin/i18n/translations/${id}/ai-suggest`, { method: 'POST' }),

  // --- Admin: log powiadomień (Faza 8.8) ---
  adminNotifications: (params) => req(`/admin/notifications${params ? `?${new URLSearchParams(params)}` : ''}`),

  // --- Admin: podgląd zadań w tle (żywe + archiwalne, z pełnym logiem) ---
  adminJobsList: (params) => req(`/admin/jobs${params ? `?${new URLSearchParams(params)}` : ''}`),
  adminJobDetail: (id) => req(`/admin/jobs/${id}`),
}
